import { Controller } from "@hotwired/stimulus"
import TableFrameNavigator from "../../utils/table_frame_navigator"
import TableQueryState from "../../utils/table_query_state"

// Connects to data-controller="insurances--filter"
// Handles filter logic and URL construction for insurances
export default class extends Controller {
  static targets = ["type", "transactionDateFrom", "transactionDateTo", "amountFrom", "amountTo", "order"]
  static outlets = ["insurances--filter-menu"]

  static singleValueTargets = ["type", "transactionDateFrom", "transactionDateTo", "amountFrom", "amountTo", "order"]

  connect() {
    this.tableNavigator = new TableFrameNavigator({
      frameId: "insurances_list",
      tableKey: "insurances",
      onPopState: (queryState) => this.setFilterValues(queryState)
    })
    this.setFilterValues()
  }

  disconnect() {
    this.tableNavigator?.disconnect()
  }

  setFilterValues(queryState = new TableQueryState(window.location.search)) {
    const urlSearchParams = queryState.toSearchParams()
    this.constructor.singleValueTargets.forEach((targetName) => {
      const hasTargetProperty = `has${targetName.charAt(0).toUpperCase() + targetName.slice(1)}Target`
      if (this[hasTargetProperty]) {
        const target = this[`${targetName}Target`]
        if (target) target.value = urlSearchParams.get(targetName) || ""
      }
    })
  }

  activeStatusChecker(event) {
    if (!this.hasInsurancesFilterMenuOutlet) {
      return
    }
    const filterMenuController = this.insurancesFilterMenuOutlet
    const mapping = {
      type: [["typeTarget"], filterMenuController.activeStatusTypeTarget],
      transactionDate: [
        ["transactionDateFromTarget", "transactionDateToTarget"],
        filterMenuController.activeStatusDateTarget
      ],
      amount: [["amountFromTarget", "amountToTarget"], filterMenuController.activeStatusAmountTarget],
      order: [["orderTarget"], filterMenuController.activeStatusOrderTarget]
    }

    for (const key in mapping) {
      if (event.target.name.includes(key)) {
        const [targets, statusTarget] = mapping[key]
        if (statusTarget) {
          this.#statusChecker(filterMenuController, targets, statusTarget)
        }
        break
      }
    }
  }

  cancelFilters() {
    this.removeValuesFromFilters()
    this.#constructUrlAndSendRequest()
  }

  removeValuesFromFilters() {
    this.constructor.singleValueTargets.forEach((targetName) => {
      const hasTargetProperty = `has${targetName.charAt(0).toUpperCase() + targetName.slice(1)}Target`
      if (this[hasTargetProperty]) {
        const target = this[`${targetName}Target`]
        target.value = ""
        target.disabled = false
      }
    })
  }

  cancelChipFilter(event) {
    const targetName = event.currentTarget.dataset.target
    const target = this[`${targetName}Target`]
    if (target) {
      target.value = ""
      target.disabled = false
      this.#constructUrlAndSendRequest()
    }
  }

  applyFilters() {
    this.#constructUrlAndSendRequest()
  }

  #constructUrlAndSendRequest() {
    this.tableNavigator.navigate(this.#constructQueryState(), {
      history: "push",
      reason: "filter",
      preserveScroll: { x: true, y: false }
    })
  }

  #statusChecker(filterMenuController, targets, statusTarget) {
    const status = targets.some((target) => {
      const targetName = target.replace("Target", "")
      const hasTargetProperty = `has${targetName.charAt(0).toUpperCase() + targetName.slice(1)}Target`
      if (!this[hasTargetProperty]) {
        return false
      }
      const element = this[target]
      if (element.type === "checkbox") {
        return element?.checked
      } else {
        return element.value
      }
    })
    filterMenuController.toggleActiveStatus(status, statusTarget)
  }

  #constructQueryState() {
    const changes = Object.fromEntries(this.constructor.singleValueTargets.map((key) => [key, null]))
    this.constructor.singleValueTargets.forEach((targetName) => {
      const hasTargetProperty = `has${targetName.charAt(0).toUpperCase() + targetName.slice(1)}Target`
      if (this[hasTargetProperty]) {
        const target = this[`${targetName}Target`]
        if (target?.value && target?.value !== "") {
          changes[targetName] = target.value
        }
      }
    })
    return new TableQueryState(window.location.search).patch(changes)
  }
}
